import { AnswerPenalty } from './answer-penalty.js';
import { ObjectiveConfig } from './objective-config.js';
import { CompletionState } from './objectives.js';

import { addTextPresentationSelector } from './util.js';

export interface ObjectiveConfigKey {
    notes: string;
    solution?: string;
    cli?: string;
}

export interface ObjectiveConfigState {
    id: string;
    name: string;
    details: string;
    points: number;
    group: string;
    optional?: boolean;
    sticky?: boolean;
    pregame?: boolean;
    icon?: string;
    unicode?: string;
    // answer key for instructors
    key?: ObjectiveConfigKey | null;
    disabled?: boolean;
    dependsOn?: string | null;
}

export interface ObjectiveCompletedState extends ObjectiveConfigState {
    completed: number;
    firstCompleted?: number;
}

export interface ObjectiveGroupOptionalCompletion {
    minObjectives: number;
    minAnswers: number;
}

export interface ObjectiveGroupConfigKey {
    notes: string;
}

// https://www.compart.com/en/unicode/U+2205 Empty Set. Add a special character here
// to prevent it from conflicting with an actual possible group id
export const ROOT_GROUP_ID = '∅ None';
export interface ObjectiveGroupConfigStateCensored {
    id: string;
    name: string;
    details: string;
    location: string;
    locationText: string;
    // Another group which must be complete for this one to show
    // defaults to preceding group
    // Undefined behavior if dependsOn group is not before this group in the list
    // If dependsOnGroup is ROOT_GROUP_ID, then this is a 'root' group which is always visible.
    dependsOnGroup?: string;
    // Must complete this number of optional objectives to proceed
    optionalCompletion?: ObjectiveGroupOptionalCompletion;
    disabled?: boolean;
}
export interface ObjectiveGroupConfigState extends ObjectiveGroupConfigStateCensored {
    // answer key for instructors
    key: ObjectiveGroupConfigKey | null;
}

export enum ObjectiveType {
    objective,
    answer
}

/*twenty
①: circled digit 1: Unicode code point: U+2460
⑩: circled unmber 10: Unicode code point: U+2469
➀: dingbat circled sans-serif digit one: Unicode code point: U+2780
➉:  dingbat circled sans-serif number ten: Unicode code point: U+2789
circled number X...
⑪: Unicode code point: U+246A
⑳: Unicode code point: U+2473
...
㉑: Unicode code point: U+3251
㉟: Unicode code point: U+325F
...
㊱: Unicode code point: U+32B1
㊿: Unicode code point: U+32BF
Unicode has no precomposed circled numbers above ㊿ (U+32BF)
*/
const circledNumberGlyphs = [
    ['➀', '➉'],
    ['⑪', '⑳'],
    ['㉑', '㉟'],
    ['㊱', '㊿']
]
    .map(cluster => {
        const result = [];
        for (
            let char = cluster[0];
            char <= cluster[1];
            char = String.fromCodePoint(char.codePointAt(0)! + 1)
        ) {
            result.push(char);
        }
        return result;
    })
    .flat();

/**
 * A display label for the answer at the given 0-based index: a
 * circled-number glyph for answers 1-50, plain decimal digits beyond
 * (there are no circled-number code points above ㊿, and plain digits
 * render in any font).
 */
export function answerNumberLabel(index: number): string {
    return circledNumberGlyphs[index] ?? String(index + 1);
}

export interface RenumberedAnswerFields {
    id: string;
    name: string;
    unicode: string;
}

/**
 * Matches the first standalone number token in a name: digits that are not
 * glued to letters or other digits and are not part of a decimal number.
 * "Question 4" and "4. port config" match; "IPv4 filtering",
 * "FortiGate 60F" and "SSL VPN 3.0" do not, so version-ish or product
 * numbers can never be mistaken for the question number.
 */
const STANDALONE_NUMBER = /(?:^|[^\w.])(\d+)(?!\w)(?!\.\d)/;

/**
 * Compute the renumbered id/name/unicode for the answer at the given
 * 0-based index.
 *
 * Only answers whose id is numeric are renumbered. Non-numeric ids
 * (e.g. generated 'ans_gen_...' ids) are preserved because renumbering
 * them would orphan team answer state, misattribute index-based
 * bonuses/penalties and break escape-room target conditions that
 * reference the ids.
 *
 * The unicode display label is assigned for every answer (display only).
 */
export function renumberAnswerFields(
    answer: Pick<ObjectiveConfigState, 'id' | 'name'>,
    index: number
): RenumberedAnswerFields {
    const numericId = /^\d+$/.test(answer.id);
    let name = answer.name;
    if (numericId) {
        const match = STANDALONE_NUMBER.exec(answer.name);
        if (match) {
            const numStr = match[1];
            if (parseInt(numStr, 10) === parseInt(answer.id, 10) + 1) {
                const numStart = match.index + match[0].length - numStr.length;
                name = `${answer.name.slice(0, numStart)}${index + 1}${answer.name.slice(
                    numStart + numStr.length
                )}`;
            }
        }
    }
    return {
        id: numericId ? `${index}` : answer.id,
        name,
        unicode: answerNumberLabel(index)
    };
}

export class Objective implements ObjectiveConfigState {
    get name() {
        return this.config ? this.config.name : '';
    }
    get details() {
        return this.config ? this.config.details : '';
    }
    get points() {
        return this.config ? this.config.points : 0;
    }
    get group() {
        return this.config ? this.config.group : '';
    }
    get pointsStr() {
        return this.config ? this.config.points : '🛇';
    }
    get optional() {
        return this.config && this.config.optional;
    }
    get sticky() {
        return this.config && this.config.sticky;
    }
    get pregame() {
        return (this.config && this.config.pregame) || false;
    }
    get icon() {
        return this.config && this.config.icon;
    }
    get unicode() {
        return this.config && this.config.unicode;
    }
    get key() {
        return this.config?.key ? { ...this.config.key } : null;
    }
    get disabled() {
        return this.config && this.config.disabled;
    }

    get objectiveLinks() {
        return this.config && this.config.objectiveLinks;
    }

    completed: number;
    firstCompleted?: number;

    constructor(
        public id: string,
        completed: CompletionState,
        private config?: ObjectiveConfig,
        public bonusIndex: number | null = null,
        public penalties?: AnswerPenalty[] | number[]
    ) {
        this.completed = completed.completed;
        this.firstCompleted = completed.firstCompleted;
        if (typeof id === 'number') {
            // eslint-disable-next-line no-debugger
            debugger;
        }
    }

    unicodeChars() {
        return addTextPresentationSelector(this.unicode);
    }
    get iconUrl() {
        return this.icon && `/assets/images/${this.icon}.svg`;
    }
    completeState() {
        return this.completed ? 'complete' : 'incomplete';
    }

    equals(other: Objective): boolean {
        return !!(
            this.id === other.id &&
            this.completed === other.completed &&
            ((!this.penalties && !other.penalties) ||
                (this.penalties &&
                    other.penalties &&
                    (<any[]>this.penalties).every((p, i) => pe(p, other.penalties?.[i] || 0))))
        );
        function pe(a: number | AnswerPenalty, b: number | AnswerPenalty): boolean {
            return (
                typeof a === typeof b &&
                (typeof a === 'number' ? a === b : (<AnswerPenalty>a).equals(<AnswerPenalty>b))
            );
        }
    }

    compare(other: Objective): string {
        const result = [];
        if (this.id !== other.id) {
            result.push(`id: ${this.config?.id} != ${other.config?.id}`);
        } else if (this.completed !== other.completed) {
            result.push(
                `id: ${this.config?.id} completed: ${this.completed} != ${other.completed}`
            );
        }
        return result.length ? result.join(', ') : '';
    }
}
